import SharedPreferences from "../constants/SharedPreferences";
import { updateAndSaveStatistics } from "./StatisticsManager";

import {
  convertArrayBufferToBase64String,
  convertBase64StringToArrayBuffer,
  getEncryptionCurveFromStorage,
  getEncryptionPolicyFromString,
  getFromStorage,
  getKeyGenerationPolicyFromString,
  getSigningCurveFromStorage,
  saveInStorage,
  toArrayBuffer,
  toBuffer,
} from "../util/Utility";
import {
  KeyGenerationPolicy,
  EncryptionPolicy,
  EncryptionMode,
} from "../BayunCore";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
  exportAesKey,
  generateAesKey,
  importAesKeyRaw,
  loadWrapAesKey,
} from "../crypto/AuthenticatedEncryption";
import { getGroupKeyFromId } from "../rest/BayunGroupManager";
import {
  loadEccSigningPrivateKey,
  loadEccSigningPublicKey,
  _generateSharedSecret,
  _loadEccPublicKey,
  _loadEccPrivateKey,
} from "../crypto/EccEncryption";
import { hmacSha256Raw, hkdfSha256Raw, asRawKey } from "../crypto/KeyMaterial";
import { encodeKey, decodeKey } from "../crypto/PBKDF2";
import Constants from "../constants/Constants";
import BayunError from "../constants/BayunError";
import { serializeThrift } from "../thrift/ThriftFormatter";
import { LockedObject } from "../thrift/LockedObject_types";

const OPERATION_LOCK = 1;
const OPERATION_UNLOCK = 2;

const crypto = require("crypto");

/**
 * This method generates and returns an encryption key.
 *
 * @param {String} sessionId id of the session in question.
 * @param {EncryptionPolicy} encryptionPolicy the type of encryption policy in use.
 * @param {String} groupId groupId is needed if the encryptionPolicy is group.
 * @returns {CryptoKey} the encryption key corresponding to the encryption policy.
 */
export const getEncryptionKey = async (
  sessionId,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
) => {
  const keysResult = await getEncryptionKeyAsString(
    sessionId,
    encryptionPolicy,
    keyGenerationPolicy,
    groupId,
  );
  await updateAndSaveStatistics(sessionId, false, false, true, true);
  // The signature keys are on the signing curve, not the encryption curve. Both loaders require
  // the curve explicitly: they used to be called without it, and the underlying config lookup
  // silently fell back to P-384, so an Ed25519 key was imported as ECDSA.
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const rawKey = asRawKey(keysResult.key) || keysResult.key;
  return {
    key: await importAesKeyRaw(rawKey),
    signatureVerificationKey: await loadEccSigningPublicKey(
      keysResult.signatureVerificationKey,
      signingCurve,
    ),
    signatureKey: await loadEccSigningPrivateKey(
      keysResult.signatureKey,
      signingCurve,
    ),
    chainRandomNo: keysResult.chainRandomNo,
    iterationNo: keysResult.iterationNo,
    encryptedBaseKey: keysResult.encryptedBaseKey,
  };
};

/**
 * This method generates and returns an encryption key.
 *
 * @param {String} sessionId id of the session in question.
 * @param {EncryptionPolicy} encryptionPolicy the type of encryption policy in use.
 * @param {String} groupId groupId is needed if the encryptionPolicy is group.
 * @returns {Object} the encryption key corresponding to the encryption policy.
 */
export const getEncryptionKeyToReturn = async (
  sessionId,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
) => {
  const keysObject = await getEncryptionKeyAsString(
    sessionId,
    encryptionPolicy,
    keyGenerationPolicy,
    groupId,
  );

  return {
    key: keysObject.key,
    signatureKey: keysObject.signatureKey,
    signatureVerificationKey: keysObject.signatureVerificationKey,
  };
};

/**
 * This method generates and returns an encryption key.
 *
 * @param {String} sessionId id of the session in question.
 * @param {EncryptionPolicy} encryptionPolicy the type of encryption policy in use.
 * @param {String} groupId groupId is needed if the encryptionPolicy is group.
 * @returns {Object} the encryption key corresponding to the encryption policy.
 */
export const getEncryptionKeyAsString = async (
  sessionId,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
) => {
  let encryptedBaseKey = null;
  let chainRandomNo = 0;
  let iterationNo = 0;

  // If encryption policy is default, get the default policy
  if (encryptionPolicy === EncryptionPolicy.DEFAULT) {
    encryptionPolicy = getEncryptionPolicyFromString(
      await getFromStorage(
        sessionId,
        SharedPreferences.DEFAULT_ENCRYPTION_POLICY,
      ),
    );
  }

  // If KGP is default, get the default policy
  if (keyGenerationPolicy === KeyGenerationPolicy.DEFAULT) {
    keyGenerationPolicy = getKeyGenerationPolicyFromString(
      await getFromStorage(
        sessionId,
        SharedPreferences.DEFAULT_KEY_GENERATION_POLICY,
      ),
    );
  }

  let baseKey = "";

  //fetching group public key by id
  // const groupData = await getGroupKeyFromId(sessionId, groupId);

  //fetching person's private key
  const memberPrivateKey = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_PRIVATE_KEY,
  );

  const groupPublicKey = await getFromStorage(
    sessionId,
    SharedPreferences.GROUP_PUBLIC_KEY + groupId,
  );

  const keysObject = await getRootKey(
    sessionId,
    encryptionPolicy,
    groupId,
    memberPrivateKey,
    groupPublicKey,
  );
  const rootKey = keysObject.key;
  const baseId = await getBaseId(sessionId, encryptionPolicy, groupId);

  if (keyGenerationPolicy === KeyGenerationPolicy.STATIC) {
    // In static KGGP, baseKey is the rootKey
    baseKey = rootKey;
  } else if (keyGenerationPolicy === KeyGenerationPolicy.ENVELOPE) {
    // In Envelop KGP, base key is a random key.
    // Base key is kept encrypted(CBC-HMAC) using the keys derived from
    // the rootKey and the baseId.
    baseKey = await generateAesKey();
    // Creating DEK using the rootKey and baseId to encryptRsaAes the baseKey
    const keyDEK = await generateDEK(rootKey, baseId);
    encryptedBaseKey = await wrapKey(baseKey, keyDEK);
  } else if (keyGenerationPolicy === KeyGenerationPolicy.CHAIN) {
    // Retrieve last ChainKGPObject from NSUserDefaults for chainKGPRandomNo &
    // last iterationNo for an encryption policy
    let lastChainObject = await getLastChainObject(sessionId, encryptionPolicy);

    if (lastChainObject) {
      chainRandomNo = lastChainObject.chainKGPRandomNo;
      iterationNo = lastChainObject.iterationNo;
    } else {
      lastChainObject = { chainKGPRandomNo: 0, iterationNo: 0 };
    }

    if (chainRandomNo === 0 || iterationNo === 100) {
      iterationNo = 0;
      chainRandomNo = getRandomNumber();
    } else {
      iterationNo += 1;
    }

    baseKey = await getChainKey(
      sessionId,
      rootKey,
      chainRandomNo,
      iterationNo,
      encryptionPolicy,
      OPERATION_LOCK,
    );
  } else {
    // when it does not get a KGP, take default as null
    keyGenerationPolicy = KeyGenerationPolicy.STATIC;
    baseKey = rootKey;
  }

  // generating the DEK using baseKey and baseId
  const encryptionKey = await generateDEK(baseKey, baseId);
  return {
    key: encryptionKey,
    signatureKey: keysObject.privateKey,
    signatureVerificationKey: keysObject.publicKey,
    encryptedBaseKey,
    chainRandomNo,
    iterationNo,
  };
};

/**
 * This method returns the root key corresponsing to the encryption policy.
 *
 * @param {String} sessionId id of the session in question.
 * @param {EncryptionPolicy} encryptionPolicy the type of encryption policy in use.
 * @param {String} groupId groupId is needed if the encryptionPolicy is group.
 * @returns {String} root key corresponding to the encryption policy.
 */
async function getRootKey(
  sessionId,
  encryptionPolicy,
  groupId,
  privateKey,
  publicKey,
) {
  if (encryptionPolicy === EncryptionPolicy.ORG) {
    return {
      key: asRawKey(await getFromStorage(sessionId, SharedPreferences.ORG_KEY)),
      publicKey: await getFromStorage(
        sessionId,
        SharedPreferences.ORG_SIGNING_PUBLIC_KEY,
      ),
      privateKey: await getFromStorage(
        sessionId,
        SharedPreferences.ORG_SIGNING_PRIVATE_KEY,
      ),
    };
  } else if (encryptionPolicy === EncryptionPolicy.MEMBER) {
    return {
      key: asRawKey(
        await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
      ),
      publicKey: await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
      ),
      privateKey: await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
      ),
    };
  } else if (encryptionPolicy === EncryptionPolicy.GROUP) {
    return await getGroupKeyFromId(sessionId, groupId);
  } else if (encryptionPolicy === EncryptionPolicy.GROUP_ASYMMETRIC) {
    // create a shared secret with given publicKey and privateKey of the random KeyPair.
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
    const eccPublicCryptoKey = await _loadEccPublicKey(
      publicKey.trim(),
      encryptionCurve,
    );
    const eccPrivateCryptoKey = await _loadEccPrivateKey(
      privateKey.trim(),
      encryptionCurve,
    );
    const sharedSecret = await _generateSharedSecret(
      eccPublicCryptoKey,
      eccPrivateCryptoKey,
      encryptionCurve,
    );

    return {
      key: sharedSecret,
      publicKey: await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
      ),
      privateKey: await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
      ),
    };
  } else if (encryptionPolicy === EncryptionPolicy.NONE) {
    return {
      key: null,
      publicKey: null,
      privateKey: null,
    };
  }
}

/**
 * This method returns the base id corresponsing to the encryption policy.
 *
 * @param {String} sessionId id of the session in question.
 * @param {EncryptionPolicy} encryptionPolicy the type of encryption policy in use.
 * @param {String} groupId groupId is needed if the encryptionPolicy is group.
 * @returns {String} base id corresponding to the encryption policy.
 */
async function getBaseId(sessionId, encryptionPolicy, groupId) {
  if (encryptionPolicy === EncryptionPolicy.ORG) {
    return await getFromStorage(sessionId, SharedPreferences.ORG_ID);
  } else if (encryptionPolicy === EncryptionPolicy.MEMBER) {
    return await getFromStorage(sessionId, SharedPreferences.MEMBER_ID);
  } else if (
    encryptionPolicy === EncryptionPolicy.GROUP_ASYMMETRIC ||
    encryptionPolicy === EncryptionPolicy.GROUP
  ) {
    return groupId;
  }
}

/**
 * HKDF-SHA256 → 32 raw bytes for AES DEK.
 * IKM = raw baseKey; salt empty; info = UTF-8(baseId). Matches Java AES KDF guidance.
 *
 * @param {Uint8Array|string} baseKey
 * @param {String} baseId
 * @returns {Promise<Uint8Array>}
 */
async function generateDEK(baseKey, baseId) {
  const ikm = asRawKey(baseKey) || baseKey;
  return await hkdfSha256Raw(ikm, null, String(baseId), 32);
}

/**
 * HKDF-SHA256 → 32 raw bytes for data authentication key.
 * Info = UTF-8(String(parseInt(baseId)+1)).
 */
async function generateAK(baseKey, baseId) {
  const increasedValue = parseInt(baseId) + 1;
  const ikm = asRawKey(baseKey) || baseKey;
  return await hkdfSha256Raw(ikm, null, String(increasedValue), 32);
}

/**
 * LMS StatisticsKey: HMAC-SHA256 raw (matches Java generateHMACKey primitive).
 * Returns raw 32 bytes for AES; callers encodeKey for API/storage.
 */
export const getLMSStatisticsKey = async (
  memberKey,
  orgName,
  orgMemberId,
  applicationId,
) => {
  if (!memberKey || !orgName || !orgMemberId || !applicationId) {
    return null;
  }

  const keyBytes = asRawKey(memberKey) || memberKey;
  return await hmacSha256Raw(
    keyBytes,
    "lms" + orgName + orgMemberId + applicationId,
  );
};

/**
 * Encrypt the file data.
 *
 * @param dataString          string to be encrypted.
 * @param encryptionPolicy    policy used for encrption
 * @param keyGenerationPolicy policy used for key generation.
 * @param encryptionMode      mode of encryption used
 * @param groupId             id of the group whose key is used for group encryption policy
 */
export const encryptData = async (
  sessionId,
  dataString,
  encryptionPolicy,
  keyGenerationPolicy,
  encryptionMode,
  groupId,
  lockImage,
  publicKey,
) => {
  let encryptedData = null;
  let encryptionKey = null;
  let encryptionKeyInfo = null;
  let iv = Buffer.from(new Int8Array(0));

  // If encryption is disabled by admin, irrespective of what policy is given,
  // policy is set to None
  if (
    (await getFromStorage(
      sessionId,
      SharedPreferences.IS_ENCRYPTION_ENABLED,
    )) === "false"
  ) {
    encryptionPolicy = EncryptionPolicy.NONE;
  }

  // If encryption policy is none, set encrypted data as it is
  if (encryptionPolicy === EncryptionPolicy.NONE) {
    if (lockImage) {
      encryptedData = window.atob(dataString);
    } else {
      encryptedData = toBuffer(new TextEncoder("utf-8").encode(dataString));
    }
  } else {
    encryptionKeyInfo = await getEncryptionKeyAsString(
      sessionId,
      encryptionPolicy,
      keyGenerationPolicy,
      groupId,
    );
    encryptionKey = encryptionKeyInfo.key;

    // encryption according to encryption mode
    try {
      if (EncryptionMode.AES_GCM === encryptionMode) {
        const baseId = await getBaseId(sessionId, encryptionPolicy, groupId);

        const increasedValue = parseInt(baseId) + 1;

        if (lockImage) {
          dataString = window.atob(dataString);
        }

        encryptedData = await aeadEncryptWithAssociatedData(
          dataString,
          `${increasedValue}`,
          encryptionKey,
        );
      }

      // else if (EncryptionMode.CBC_HMAC === encryptionMode) {
      //   // baseKey is used to generate the authentication key (AK) using HMAC-SHA256
      //   const authenticationKey = generateAK(baseKey, baseId);
      //   console.log("TODO: Missing RNcryptor library for JS");
      // }
      else {
        if (EncryptionMode.CBC === encryptionMode) {
          iv = Buffer.from(window.crypto.getRandomValues(new Int8Array(16)));
          encryptedData = aes256Encrypt(
            encryptionMode,
            dataString,
            encryptionKey,
            iv,
            lockImage,
          );
        } else {
          encryptedData = aes256Encrypt(
            encryptionMode,
            dataString,
            encryptionKey,
            iv,
            lockImage,
          );
        }
      }
    } catch (error) {
      console.log(error);
      throw BayunError.ERROR_ENCRYPTION_FAILED;
    }
  }

  var encryptedBaseKey = null;
  var chainRandomNo = null;
  var iterationNo = null;

  if (encryptionKeyInfo) {
    encryptionKey = encryptionKeyInfo.key;
    encryptedBaseKey = encryptionKeyInfo.encryptedBaseKey;
    chainRandomNo = encryptionKeyInfo.chainRandomNo;
    iterationNo = encryptionKeyInfo.iterationNo;
  }

  // adding to lockedObject and serializing it
  const lockedObject = new LockedObject({
    version: 2,
    encryptedData,
    keyGenerationPolicy,
    encryptionPolicy,
    encryptionMode,
    groupId,
    encryptedDEK: toBuffer(convertBase64StringToArrayBuffer(encryptedBaseKey)),
    iv,
    chainKGPRandomNo: chainRandomNo,
    iterationNumber: iterationNo,
    publicKey,
  });
  return serializeThrift(lockedObject);
};

/**
 * Decrypts the data encrypted with Bayun.
 *
 * @param {String} sessionId id of the session in question.
 * @param {LockedObject} lockedObject the thrift object containing metadata and ciphertext.
 * @returns decrypted message as a String
 */
export const decryptData = async (sessionId, lockedObject, unlockImage) => {
  const encryptedData = lockedObject.encryptedData;
  const lockedObjectVersion = lockedObject.version;
  const keyGenerationPolicy = lockedObject.keyGenerationPolicy;
  const decryptionPolicy = lockedObject.encryptionPolicy;
  const decryptionMode = lockedObject.encryptionMode;
  const groupId = lockedObject.groupId;
  const publicKey = lockedObject.publicKey;

  let groupPrivateKey = "";

  if (
    decryptionPolicy == EncryptionPolicy.GROUP ||
    decryptionPolicy == EncryptionPolicy.GROUP_ASYMMETRIC
  ) {
    const groupKeys = await getGroupKeyFromId(sessionId, groupId);
    if (groupKeys != null) {
      groupPrivateKey = groupKeys.privateKey;
    }
  }

  //Root Key and BaseId are determined based on encryption-policy, groupId
  const baseId = await getBaseId(sessionId, decryptionPolicy, groupId);
  const keysObject = await getRootKey(
    sessionId,
    decryptionPolicy,
    groupId,
    groupPrivateKey,
    publicKey,
  );
  const rootKey = keysObject.key;
  let baseKey = null;
  let decryptedData = null;
  let iv = Buffer.from(new Uint8Array(0));

  const encryptedString = new TextDecoder().decode(
    toArrayBuffer(encryptedData),
  );

  // if encryption is none, no encryption was done hence return
  // encryptedData as it is
  if (decryptionPolicy === EncryptionPolicy.NONE) {
    if (unlockImage) {
      return Buffer.from(lockedObject.encryptedData).toString("base64");
    } else {
      return encryptedString;
    }
    //return encryptData;
  }

  if (lockedObjectVersion === 1) {
    // rootKey is used to generate the data-encryption-key(DEK)
    baseKey = rootKey;
  } else if (lockedObjectVersion === 2) {
    if (keyGenerationPolicy === KeyGenerationPolicy.STATIC) {
      // rootKey is used to generate the data-encryption-key(DEK)
      baseKey = rootKey;
    } else if (keyGenerationPolicy === KeyGenerationPolicy.ENVELOPE) {
      // retrive the encrypted Base Key
      const encryptedBaseKeyData = lockedObject.encryptedDEK;
      // create DEK using the root key and baseId to decryptRsaAes the baseKey
      const keyDEK = await generateDEK(rootKey, baseId);
      // unwrap the base key
      baseKey = await unwrapKey(encryptedBaseKeyData, keyDEK);
    } else if (keyGenerationPolicy === KeyGenerationPolicy.CHAIN) {
      const chainRandomNo = lockedObject.chainKGPRandomNo;
      const iterationNo = lockedObject.iterationNumber;
      baseKey = await getChainKey(
        sessionId,
        rootKey,
        chainRandomNo,
        iterationNo,
        decryptionPolicy,
        OPERATION_UNLOCK,
      );
    }
  }

  // create the data decryption key using base key and baseId
  const decryptionKey = await generateDEK(baseKey, baseId);

  // decryption based on encryption mode
  if (decryptionMode === EncryptionMode.AES_GCM) {
    const increasedValue = parseInt(baseId) + 1;
    decryptedData = await aeadDecryptWithAssociatedData(
      encryptedString,
      `${increasedValue}`,
      decryptionKey,
    );

    // aeadDecryptWithAssociatedData returns undefined when the AES-GCM auth
    // tag check fails (tampered data / wrong key). Surface it instead of
    // silently returning undefined.
    if (decryptedData === undefined || decryptedData === null) {
      throw BayunError.ERROR_SOMETHING_WENT_WRONG;
    }

    if (unlockImage) {
      decryptedData = window.btoa(decryptedData);
    }
  }

  // else if (decryptionMode === EncryptionMode.CBC_HMAC) {
  //   const authenticationKey = generateAK(baseKey, baseId);
  //   console.log("TODO: Missing RNcryptor library for JS");
  // }
  else {
    if (decryptionMode === EncryptionMode.CBC) {
      iv = lockedObject.iv;
      decryptedData = aes256Decrypt(
        decryptionMode,
        encryptedData,
        decryptionKey,
        iv,
        unlockImage,
      );
    } else {
      decryptedData = aes256Decrypt(
        decryptionMode,
        encryptedData,
        decryptionKey,
        iv,
        unlockImage,
      );
    }
  }

  return decryptedData;
};

/**
 * Wraps a key with another key.
 *
 * @param baseKeyString Key to be wrapped.
 * @param keyDEKString  Key to wrap with.
 * @return wrapped key as Uint8Array.
 */
async function wrapKey(baseKeyMaterial, keyDEKMaterial) {
  const baseKey = await importAesKeyRaw(asRawKey(baseKeyMaterial) || baseKeyMaterial);
  const keyDEK = await loadWrapAesKey(asRawKey(keyDEKMaterial) || keyDEKMaterial);

  const wrapped = await window.crypto.subtle.wrapKey(
    "raw",
    baseKey,
    keyDEK,
    "AES-KW",
  );

  const wrappedKeyBuffer = new Uint8Array(wrapped);
  return convertArrayBufferToBase64String(wrappedKeyBuffer);
}

async function unwrapKey(encryptedBaseKey, keyDEKMaterial) {
  const keyDEK = await loadWrapAesKey(asRawKey(keyDEKMaterial) || keyDEKMaterial);
  const unwrapped = await window.crypto.subtle.unwrapKey(
    "raw",
    encryptedBaseKey,
    keyDEK,
    "AES-KW",
    "AES-GCM",
    true,
    ["encrypt", "decrypt"],
  );
  return exportAesKey(unwrapped);
}

/**
 * This method encrypts the data using AES256 for CBC or ECB mode.
 *
 * @param encryptionMode the mode of encryption to be used. It can be CBC or ECB.
 * @param data data to be encrypted.
 * @param key key with which it is to be encrypted.
 * @param iv the IV needed for encryption.
 * @returns return the encrypted data as a uint8 buffer.
 */
function aes256Encrypt(encryptionMode, data, key, iv, lockImage) {
  // console.log(encryptionMode, data, key, iv);
  let encryptionAlgorithm = "aes-256-cbc";

  if (encryptionMode === EncryptionMode.ECB) {
    encryptionAlgorithm = "aes-256-ecb";
  }

  const keyBuf = Buffer.isBuffer(key)
    ? key
    : Buffer.from(asRawKey(key) || key);

  const cipher = crypto.createCipheriv(encryptionAlgorithm, keyBuf, iv);

  var returnString;
  if (lockImage) {
    returnString =
      cipher.update(data, "base64", "base64") + cipher.final("base64");
  } else {
    returnString =
      cipher.update(data, "utf8", "base64") + cipher.final("base64");
  }

  return toBuffer(convertBase64StringToArrayBuffer(returnString));
}

/**
 * This method decrypts the data using AES256 for CBC or ECB mode.
 *
 * @param encryptionMode the mode of decryption to be used. It can be CBC or ECB.
 * @param data data to be decrypted.
 * @param key key with which it is to be encrypted.
 * @param iv the IV needed for decryption.
 * @returns return the encrypted data as a uint8 buffer.
 */
function aes256Decrypt(encryptionMode, data, key, iv, unlockImage) {
  let encryptionAlgorithm = "aes-256-cbc";

  if (encryptionMode === EncryptionMode.ECB) {
    encryptionAlgorithm = "aes-256-ecb";
  }

  const keyBuf = Buffer.isBuffer(key)
    ? key
    : Buffer.from(asRawKey(key) || key);

  const cipher = crypto.createDecipheriv(encryptionAlgorithm, keyBuf, iv);

  if (unlockImage) {
    // IMP : This will be used for cross platform testing of files
    return cipher.update(data, "base64", "base64") + cipher.final("base64");
  } else {
    return cipher.update(data, "base64", "utf8") + cipher.final("utf8");
  }
}

/**
 * Get last saved chain object for the given encryption policy.
 *
 * @param encryptionPolicy encryption policy for the last chain object.
 * @return the last chain object.
 */
async function getLastChainObject(sessionId, encryptionPolicy) {
  const key = getChainKGPObjectKey(encryptionPolicy);

  if (key) {
    const jsonObjectString = await getFromStorage(
      sessionId,
      SharedPreferences.LAST_CHAIN_OBJECT_FOR + key,
    );

    if (jsonObjectString) {
      return JSON.parse(jsonObjectString);
    }
  }
  return null;
}

/**
 * Get Chain object key for the particular policy.
 *
 * @param policy encryption policy for which key is requested.
 * @return chain object key.
 */
function getChainKGPObjectKey(encryptionPolicy) {
  if (encryptionPolicy === EncryptionPolicy.ORG) {
    return Constants.KGP_ORG;
  } else if (encryptionPolicy === EncryptionPolicy.MEMBER) {
    return Constants.KGP_MEMBER;
  } else if (encryptionPolicy === EncryptionPolicy.GROUP) {
    return Constants.KGP_GROUP;
  }
  return null;
}

/**
 * Generate a random number, equivalent to arc4random().
 *
 * @return random number created.
 */
function getRandomNumber() {
  // Must be non-zero: a chainRandomNo of 0 is used as the "start a new chain"
  // sentinel elsewhere, so returning 0 here would corrupt chain-KGP tracking.
  return Math.floor(Math.random() * 100) + 1;
}

/**
 * Get the chain key for base key of encryption/decryption.
 *
 * @param rootKey          root key needed to generate the HMAC key(s).
 * @param chainRandomNo    random number for chain object.
 * @param iterationNo      iteration number for chain object for an encryption policy.
 * @param encryptionPolicy encryption policy for the chain object.
 * @param operation        lock or unlock operation.
 * @return chian key that serves as base key for encryption/decryption.
 */
async function getChainKey(
  sessionId,
  rootKey,
  chainRandomNo,
  iterationNo,
  encryptionPolicy,
  operation,
) {
  //if base key already exists for chainKGPRandomNo and iterationNo
  let chainKey = null;
  let chainObjects = await getAllChainObjects(sessionId);

  if (operation === OPERATION_UNLOCK) {
    //same object might get decrypted for number of times, therefore cached key can be used.
    chainKey = getExistingChainKey(
      chainRandomNo,
      iterationNo,
      encryptionPolicy,
      chainObjects,
    );
  }

  if (!chainKey) {
    if (iterationNo > 0) {
      const existingChainkey = getExistingChainKey(
        chainRandomNo,
        iterationNo - 1,
        encryptionPolicy,
        chainObjects,
      );

      if (existingChainkey) {
        chainKey = await generateChainKey(
          sessionId,
          existingChainkey,
          chainRandomNo,
          0,
          encryptionPolicy,
          operation,
          true,
        );
      }
    }

    if (!chainKey) {
      chainKey = await generateChainKey(
        sessionId,
        rootKey,
        chainRandomNo,
        iterationNo,
        encryptionPolicy,
        operation,
        false,
      );
    }
  }

  return chainKey;
}

/**
 * Get all chain objects from shared preferences.
 *
 * @return array list of chain objects.
 */
async function getAllChainObjects(sessionId) {
  const jsonChainObjectList = await getFromStorage(
    sessionId,
    SharedPreferences.CHAIN_KGP_OBJECTS_LIST,
  );

  if (jsonChainObjectList) {
    return JSON.parse(jsonChainObjectList);
  } else {
    return [];
  }
}

/**
 * Search and return base key of an existing chain key object.
 *
 * @param chainRandomNo    random number for key generation.
 * @param iterationNo      iteration number for the encryption policy.
 * @param encryptionPolicy encryption policy for key generation.
 * @param chainObjectsList array list of all existing chain objects.
 * @return base key of the existing chain key object.
 */
function getExistingChainKey(
  chainRandomNo,
  iterationNo,
  encryptionPolicy,
  chainObjectsList,
) {
  if (chainObjectsList) {
    for (let i = 0; i < chainObjectsList.length; i++) {
      const chainObject = chainObjectsList[i];
      if (
        chainObject.chainKGPRandomNo === chainRandomNo &&
        chainObject.iterationNumber === iterationNo &&
        chainObject.encryptionPolicy === encryptionPolicy
      ) {
        return asRawKey(chainObject.baseKey) || chainObject.baseKey;
      }
    }
    return null;
  }
  return null;
}

/**
 * Generate a chain key according to its iteration number.
 *
 * @param rootKey           root key for generating HMAC keys.
 * @param chainRandomNo     random number, needed for key generation.
 * @param iterationNo       iteration number increases per encryption for an
 *                          encryption policy.
 * @param encryptionPolicy  policy with which the key to be generated.
 * @param operation         lock or unlock operation.
 * @param isExistingRootKey if root key exists.
 * @return final chain key.
 */
async function generateChainKey(
  sessionId,
  rootKey,
  chainRandomNo,
  iterationNo,
  encryptionPolicy,
  operation,
  isExistingRootKey,
) {
  const randomNumberString = String(chainRandomNo);

  if (iterationNo == 0) {
    const chainKey = await hmacSha256Raw(
      asRawKey(rootKey) || rootKey,
      randomNumberString,
    );

    if (!isExistingRootKey) {
      // saving the object with iteration number 0, only when key is getting generated from
      // the starting and not when an existing chain key is used to generate the next chain key.
      // In that case object is saved outside this method with actual iterationNo.
      await saveChainObject(
        sessionId,
        chainKey,
        chainRandomNo,
        iterationNo,
        encryptionPolicy,
        operation,
      );
    }

    return chainKey;
  }

  const intermediateChainKey = await generateChainKey(
    sessionId,
    rootKey,
    chainRandomNo,
    iterationNo - 1,
    encryptionPolicy,
    operation,
    isExistingRootKey,
  );

  const finalChainKey = await hmacSha256Raw(
    asRawKey(intermediateChainKey) || intermediateChainKey,
    randomNumberString,
  );

  await saveChainObject(
    sessionId,
    finalChainKey,
    chainRandomNo,
    iterationNo,
    encryptionPolicy,
    operation,
  );

  return finalChainKey;
}

/**
 * Saves the chain object.
 *
 * @param chainKey         chain key to be saved as root key.
 * @param chainRandomNo    random number for key creation.
 * @param iterationNo      iteration number.
 * @param encryptionPolicy encryption policy for key creation.
 * @param operation        lock or unlock operation.
 */
async function saveChainObject(
  sessionId,
  chainKey,
  chainRandomNo,
  iterationNo,
  encryptionPolicy,
  operation,
) {
  const chainObject = {
    iterationNo,
    chainKGPRandomNo: chainRandomNo,
    baseKey: encodeKey(asRawKey(chainKey) || chainKey),
    encryptionPolicy,
  };

  await saveLastChainObject(sessionId, chainObject);

  // save last ChainKGPObject in local storage for
  // keeping track of chainKGPRandomNo & iterationNo.
  if (operation === OPERATION_LOCK) {
    await saveLastChainObjectInLocalStorage(sessionId, chainObject);
  }
}

/**
 * Saves last chain object by adding it to the all objects array list.
 *
 * @param chainObject object to be added.
 */
async function saveLastChainObject(sessionId, chainObject) {
  const key = getChainKGPObjectKey(chainObject.encryptionPolicy);

  if (key) {
    let chainObjectsList = await getAllChainObjects(sessionId);

    if (!chainObjectsList) {
      chainObjectsList = [];
    }

    chainObjectsList.push(chainObject);

    await saveArrayListInLocalStorage(sessionId, chainObjectsList);
  }
}

/**
 * Saves chain objects array list in local storage.
 *
 * @param chainObjectsList array list of ChainKGPObjects.
 */
async function saveArrayListInLocalStorage(sessionId, chainObjectsList) {
  await saveInStorage(
    sessionId,
    SharedPreferences.CHAIN_KGP_OBJECTS_LIST,
    JSON.stringify(chainObjectsList),
  );
}

/**
 * Save object as last object for an encryption policy in local storage.
 *
 * @param chainObject object to be saved.
 */
async function saveLastChainObjectInLocalStorage(sessionId, chainObject) {
  if (chainObject) {
    await saveInStorage(
      sessionId,
      SharedPreferences.LAST_CHAIN_OBJECT_FOR +
        getChainKGPObjectKey(chainObject.encryptionPolicy),
      JSON.stringify(chainObject),
    );
  }
}

/**
 * Uses JNCryptor to encryptRsaAes for HMAC encryption mode
 *
 * @param data              text to be encrypted
 * @param encryptionKey     key used for encryption
 * @param authenticationKey authentication key for encryption
 * @return encrypted data as byte[]
 */
// async function encryptWithAuthentication(
//   data,
//   encryptionKey,
//   authenticationKey
// ) {
//   const encryptedData = RNCryptor.EncryptWithArbitraryKeys(
//     data,
//     encryptionKey,
//     authenticationKey
//   );

//   return encryptedData;
// }
